import { UCI_RE } from "@/lib/chess/rules";
import type { ChessMateDefinition, ChessMateProgress, ChessMateSubmission, Line } from "./types";

const DEFAULT_RULES = {
  en: "Find the forced checkmate in the stated number of moves, against any defence. Play your moves on the board and submit.",
  fr: "Trouvez le mat forcé en le nombre de coups indiqué, quelle que soit la défense. Jouez vos coups sur l'échiquier et soumettez.",
};

export const EMPTY_FEN = "8/8/8/8/8/8/8/8 w - - 0 1";

export function emptyDefinition(): ChessMateDefinition {
  return {
    puzzleType: "chess_mate",
    typeVersion: 1,
    fen: EMPTY_FEN,
    mateIn: 2,
    answer: "key",
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: ChessMateDefinition): ChessMateProgress {
  return { version: 1, line: null };
}

/** Reads a stored line, dropping anything that is not a UCI move; null if the root is not one. */
export function sanitizeLine(raw: unknown): Line | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const { move, defences } = raw as { move?: unknown; defences?: unknown };
  if (typeof move !== "string" || !UCI_RE.test(move)) return null;
  const line: Line = { move };
  if (Array.isArray(defences)) {
    const kept = defences.flatMap((d) => {
      if (typeof d !== "object" || d === null) return [];
      const { move: reply, line: sub } = d as { move?: unknown; line?: unknown };
      const child = sanitizeLine(sub);
      return typeof reply === "string" && UCI_RE.test(reply) && child
        ? [{ move: reply, line: child }]
        : [];
    });
    if (kept.length > 0) line.defences = kept;
  }
  return line;
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: ChessMateDefinition, raw: unknown): ChessMateProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const { line, move } = raw as { line?: unknown; move?: unknown };
  // A blob from before the tree carried the key alone as `move`.
  if (line === undefined && typeof move === "string" && UCI_RE.test(move))
    return { version: 1, line: { move } };
  return { version: 1, line: sanitizeLine(line) };
}

/** No move played yet is sent as an empty root: the server answers `invalid_submission`. */
export function serializeSubmission(
  _definition: ChessMateDefinition,
  progress: ChessMateProgress,
): ChessMateSubmission {
  return { line: progress.line ?? { move: "" } };
}
